import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { revisarLote, leerExcelLote, crearLote } from '../../api/client'
import { filasDesdeTexto, BLOQUEAN } from './loteLogica'
import { hoyLocal } from '../partes/fechas'
import SelectorProyecto from '../SelectorProyecto'

const FILA = () => ({ articulo_codigo: '', articulo_descripcion: '', cantidad: '' })

// "Nueva solicitud" > Varios artículos (lote): many items at once, each its
// own OT and OF. The shared data once; one row per item, pasted from Excel
// (code, description, quantity) or read from an .xlsx. Only items with their
// recipe in SAP (no BOM or plan asked). The rows are checked as they change
// (repeated or missing code, no quantity, an OT already open for the code);
// sending creates and sends every OT, or none. origen: proyecto | reposicion.
export default function FormLote({ origen, proyectos, grupos }) {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const archivo = useRef(null)
  const [f, setF] = useState({ proyecto_id: '', fecha_requerida: '', tipo: 'fabricacion', grupo_propuesto_id: '', descripcion: '' })
  const [filas, setFilas] = useState([FILA()])
  const [revision, setRevision] = useState([])
  const [faltan, setFaltan] = useState([])
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  const usadas = filas.filter(x => x.articulo_codigo.trim() || x.articulo_descripcion.trim() || x.cantidad !== '')
  // Check the rows against the server (repeated, missing, already open) as they change.
  useEffect(() => {
    if (!usadas.length) return undefined
    const id = setTimeout(() => {
      revisarLote({ filas: usadas.map(x => ({ ...x, cantidad: x.cantidad === '' ? null : Number(x.cantidad) })) })
        .then(setRevision).catch(() => {})
    }, 400)
    return () => clearTimeout(id)
  }, [filas]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); setFaltan(fs => fs.filter(n => n !== k)) }
  const setCelda = (i, k, v) => setFilas(fs => fs.map((x, j) => (j === i ? { ...x, [k]: v } : x)))
  const agregar = (nuevas) => {
    const limpias = nuevas.map(x => ({ articulo_codigo: x.articulo_codigo || '', articulo_descripcion: x.articulo_descripcion || '',
      cantidad: x.cantidad == null ? '' : String(x.cantidad) }))
    setFilas(fs => [...fs.filter(x => x.articulo_codigo.trim() || x.articulo_descripcion.trim() || x.cantidad !== ''), ...limpias, FILA()])
  }
  // Pasting several cells from Excel anywhere in the table adds them as rows.
  const pegar = (e) => {
    const texto = e.clipboardData.getData('text')
    if (!texto.includes('\t') && !texto.includes('\n')) return
    e.preventDefault()
    agregar(filasDesdeTexto(texto))
  }
  const subir = async (file) => {
    setError('')
    try { agregar(await leerExcelLote(file)) } catch (err) { setError(err.response?.data?.detail?.message || t('ot.lote.excelFallo')) }
  }

  // The check of row i among the used ones (the server numbers them from 1).
  const estadoDe = (i) => {
    const pos = usadas.indexOf(filas[i])
    return pos >= 0 ? revision[pos] : null
  }
  const bloqueadas = usadas.filter((_, k) => BLOQUEAN.includes(revision[k]?.estado)).length

  const enviar = async () => {
    const fs = []
    if (origen === 'proyecto' && !f.proyecto_id) fs.push('proyecto')
    if (!f.fecha_requerida) fs.push('fecha_requerida')
    if (fs.length || !usadas.length) {
      setFaltan(fs)
      setError(!usadas.length ? t('ot.lote.sinFilas') : t('ot.faltanDatos', { lista: fs.map(x => t(`ot.faltan.${x}`)).join(', ') }))
      return
    }
    setEnviando(true); setError('')
    try {
      const lote = await crearLote({
        origen, tipo: f.tipo, proyecto_id: origen === 'proyecto' ? Number(f.proyecto_id) : null,
        fecha_requerida: f.fecha_requerida, grupo_propuesto_id: f.grupo_propuesto_id ? Number(f.grupo_propuesto_id) : null,
        descripcion: f.descripcion, filas: usadas.map(x => ({ ...x, cantidad: x.cantidad === '' ? null : Number(x.cantidad) })),
      })
      navigate(`/operations/planta/ot?vista=lista&buscar=${encodeURIComponent(lote.codigo)}`)
    } catch (err) {
      const d = err.response?.data?.detail
      if (d?.code === 'lote_invalido') { setRevision(d.filas); setError(t('ot.lote.corregir')) }
      else if (d?.code === 'validacion') setError(t('ot.faltanDatos', { lista: (d.faltan || []).map(x => t(`ot.faltan.${x}`, { defaultValue: x })).join(', ') }))
      else setError(d?.message || t('ot.panel.fallo'))
    }
    setEnviando(false)
  }

  const lbl = 'block text-xs font-medium text-gray-700 mb-1'
  const inp = (k) => `input ${faltan.includes(k) ? '!border-rose-400' : ''}`
  const celda = 'w-full bg-transparent px-1.5 py-1 text-sm focus:outline-none focus:bg-white focus:ring-1 focus:ring-gray-300 rounded'

  return (
    <div className="space-y-5">
      <section className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">{t('ot.lote.comunes')}</h2>
        <div className="grid sm:grid-cols-4 gap-3">
          {origen === 'proyecto' && (
            <div>
              <label className={lbl}>{t('ot.campos.proyecto')} *</label>
              <SelectorProyecto className={inp('proyecto')} proyectos={proyectos} value={f.proyecto_id} onChange={v => set('proyecto_id', v)}
                placeholder={t('ot.nueva.buscarProyecto')} textoVacio={t('ot.nueva.elegir')} />
            </div>
          )}
          <div>
            <label className={lbl}>{t('ot.campos.fecha_requerida')} *</label>
            <input type="date" min={hoyLocal()} className={inp('fecha_requerida')} value={f.fecha_requerida}
              onChange={e => set('fecha_requerida', e.target.value)} />
          </div>
          <div>
            <label className={lbl}>{t('ot.campos.tipo')}</label>
            <select className="input" value={f.tipo} onChange={e => set('tipo', e.target.value)}>
              {['fabricacion', 'estructura'].map(x => <option key={x} value={x}>{t(`ot.tipos.${x}`)}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>{t('ot.campos.grupo_propuesto')}</label>
            <select className="input" value={f.grupo_propuesto_id} onChange={e => set('grupo_propuesto_id', e.target.value)}>
              <option value="">{t('ot.nueva.sinGrupo')}</option>
              {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className={lbl}>{t('ot.campos.descripcion')}</label>
          <input className="input" value={f.descripcion} placeholder={t('ot.lote.descripcionEj')} onChange={e => set('descripcion', e.target.value)} />
        </div>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-5" onPaste={pegar}>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-gray-900">{t('ot.lote.articulos', { n: usadas.length })}</h2>
          <div className="flex gap-2">
            <button type="button" onClick={() => archivo.current?.click()}
              className="text-xs font-medium text-gray-700 border border-gray-200 rounded-md px-2.5 py-1 hover:bg-gray-50">
              {t('ot.lote.subirExcel')}
            </button>
            <input ref={archivo} type="file" accept=".xlsx" className="hidden"
              onChange={e => { if (e.target.files[0]) subir(e.target.files[0]); e.target.value = '' }} />
          </div>
        </div>
        <p className="text-[11px] text-gray-500 mb-2">{t('ot.lote.pegarAyuda')}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-anchos-propios>
            <thead>
              <tr className="text-left text-[11px] text-gray-500 border-b border-gray-100">
                <th className="w-8 py-1.5">#</th>
                <th className="w-44 py-1.5">{t('ot.campos.articulo_codigo')}</th>
                <th className="py-1.5">{t('ot.campos.articulo_descripcion')}</th>
                <th className="w-20 py-1.5">{t('ot.campos.cantidad')}</th>
                <th className="w-44 py-1.5"></th>
                <th className="w-6"></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((x, i) => {
                const r = estadoDe(i)
                return (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="text-[11px] text-gray-400">{i + 1}</td>
                    <td><input className={`${celda} font-mono`} value={x.articulo_codigo} placeholder="EFAH000101"
                      onChange={e => setCelda(i, 'articulo_codigo', e.target.value)} /></td>
                    <td><input className={celda} value={x.articulo_descripcion} onChange={e => setCelda(i, 'articulo_descripcion', e.target.value)} /></td>
                    <td><input type="number" min="0" step="any" className={celda} value={x.cantidad}
                      onChange={e => setCelda(i, 'cantidad', e.target.value)} /></td>
                    <td><EstadoFila r={r} t={t} /></td>
                    <td>
                      {filas.length > 1 && (
                        <button type="button" onClick={() => setFilas(fs => fs.filter((_, j) => j !== i))}
                          className="text-gray-300 hover:text-red-500" title={t('ot.lote.quitarFila')}>×</button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={() => setFilas(fs => [...fs, FILA()])} className="mt-2 text-xs font-medium text-indigo-600 hover:underline">
          + {t('ot.lote.agregarFila')}
        </button>
        <p className="text-[11px] text-gray-500 mt-3">ⓘ {t('ot.lote.conReceta')}</p>
      </section>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={() => navigate('/operations/planta/ot')}
          className="border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
          {t('common:actions.cancel')}
        </button>
        <button type="button" disabled={enviando || bloqueadas > 0 || !usadas.length} onClick={enviar}
          title={bloqueadas ? t('ot.lote.corregir') : undefined}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
          {enviando ? t('common:actions.saving') : origen === 'reposicion'
            ? t('ot.lote.enviarPropuesta', { n: usadas.length }) : t('ot.lote.enviar', { n: usadas.length })}
        </button>
      </div>
    </div>
  )
}

function EstadoFila({ r, t }) {
  if (!r) return null
  const tono = r.estado === 'ok' ? 'bg-emerald-50 text-emerald-700'
    : r.estado === 'abierta' ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-700'
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] ${tono}`}>
      {t(`ot.lote.estado.${r.estado}`, { detalle: r.detalle })}
    </span>
  )
}
